import {
  ENABLE_ADDITIONAL_REQUESTS_URL,
  MANAGE_BILLING_URL,
  PREMIUM_QUOTA_TRIGGER,
  UPGRADE_FREEZE_LEARN_MORE_URL,
  UPGRADE_PLAN_URL,
} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {useEntitlement} from '@github-ui/copilot-entitlement/EntitlementContext'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {Banner, Link, LinkButton} from '@primer/react'
import {type ReactNode, useEffect, useState} from 'react'

const triggerValue = PREMIUM_QUOTA_TRIGGER

export function useQuotaMeterBannerVisibility() {
  const {
    isLicensedLimited,
    chatQuotaRemaining,
    premiumChatQuotaRemaining,
    premiumInteractionsQuotaExceeded,
    overagesEnabled,
    tokenBasedBillingEnabled,
  } = useEntitlement()
  const [isVisible, setIsVisible] = useState<boolean>(() => {
    return copilotLocalStorage.getMeterBannerFlag() ?? false
  })

  useEffect(() => {
    if (chatQuotaRemaining !== undefined) {
      const existingFlag = copilotLocalStorage.getMeterBannerFlag()

      if (
        (chatQuotaRemaining > 0 && chatQuotaRemaining <= triggerValue) ||
        (!isLicensedLimited && premiumChatQuotaRemaining > 0 && premiumChatQuotaRemaining <= triggerValue) ||
        (!isLicensedLimited && premiumInteractionsQuotaExceeded && overagesEnabled)
      ) {
        if (existingFlag === null) {
          copilotLocalStorage.setMeterBannerFlag(true)
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setIsVisible(true)
        } else {
          setIsVisible(existingFlag)
        }
      } else {
        copilotLocalStorage.removeMeterBannerFlag()
        setIsVisible(false)
      }
    }
  }, [
    isLicensedLimited,
    chatQuotaRemaining,
    premiumChatQuotaRemaining,
    overagesEnabled,
    premiumInteractionsQuotaExceeded,
  ])

  const handleDismiss = () => {
    copilotLocalStorage.setMeterBannerFlag(false)
    setIsVisible(false)
  }

  return {
    visible: isVisible && chatQuotaRemaining !== undefined && !tokenBasedBillingEnabled,
    onDismiss: handleDismiss,
  }
}

interface QuotaMeterBannerProps {
  onDismiss: () => void
  className?: string
}

export function QuotaMeterBanner({onDismiss, className}: QuotaMeterBannerProps) {
  const {
    isLicensedLimited,
    canUpgradePlan,
    canPurchaseAdditionalQuota,
    premiumInteractionsQuotaExceeded,
    resetDate,
    plan,
    chatQuotaRemaining,
    premiumChatQuotaRemaining,
  } = useEntitlement()

  const quotaUsage = isLicensedLimited ? 100 - (chatQuotaRemaining ?? 0) : 100 - (premiumChatQuotaRemaining ?? 0)

  const upgradeText =
    !copilotFeatureFlags.upgradeFreezeEnabled && canUpgradePlan
      ? ' Upgrade to increase your limit.'
      : ' Enable additional requests to get more usage after the limit is reached.'
  const baseDescription = isLicensedLimited
    ? `You have used ${100 - triggerValue}% of your free requests this month.`
    : !premiumInteractionsQuotaExceeded
      ? `You have used ${100 - triggerValue}% of your premium responses this month.${upgradeText}`
      : `You have used all premium requests available this month. You will be charged per additional premium request until the limit resets on ${resetDate}.`

  const showFreezeMessageInDescription =
    copilotFeatureFlags.upgradeFreezeEnabled && canUpgradePlan && !premiumInteractionsQuotaExceeded

  const description: ReactNode = showFreezeMessageInDescription ? (
    <>
      {baseDescription} Plan upgrades are temporarily unavailable.{' '}
      <Link inline href={UPGRADE_FREEZE_LEARN_MORE_URL}>
        Read more
      </Link>
    </>
  ) : (
    baseDescription
  )

  const primaryAction =
    canUpgradePlan && !premiumInteractionsQuotaExceeded ? (
      copilotFeatureFlags.upgradeFreezeEnabled ? undefined : (
        <LinkButton
          as="a"
          href={UPGRADE_PLAN_URL}
          onClick={() => {
            sendEvent('dotcom_chat.activate', {
              target: 'QUOTA_METER_BANNER_LINK_UPGRADE',
              mode: 'immersive',
              tokenBasedBillingEnabled: false,
              plan,
              quotaUsage,
            })
          }}
        >
          Upgrade
        </LinkButton>
      )
    ) : canPurchaseAdditionalQuota && !premiumInteractionsQuotaExceeded ? (
      <LinkButton
        as="a"
        href={ENABLE_ADDITIONAL_REQUESTS_URL}
        onClick={() => {
          sendEvent('dotcom_chat.activate', {
            target: 'QUOTA_METER_BANNER_LINK_OVERAGES',
            mode: 'immersive',
            tokenBasedBillingEnabled: false,
            plan,
            quotaUsage,
          })
        }}
      >
        Enable additional requests
      </LinkButton>
    ) : (
      canPurchaseAdditionalQuota && (
        <LinkButton
          as="a"
          href={MANAGE_BILLING_URL}
          onClick={() => {
            sendEvent('dotcom_chat.activate', {
              target: 'QUOTA_METER_BANNER_LINK_MANAGE_BILLING',
              mode: 'immersive',
              tokenBasedBillingEnabled: false,
              plan,
              quotaUsage,
            })
          }}
        >
          Manage billing
        </LinkButton>
      )
    )

  const secondaryAction =
    canUpgradePlan && canPurchaseAdditionalQuota && !premiumInteractionsQuotaExceeded ? (
      <Link
        as="a"
        href={ENABLE_ADDITIONAL_REQUESTS_URL}
        onClick={() => {
          sendEvent('dotcom_chat.activate', {
            target: 'QUOTA_METER_BANNER_LINK_OVERAGES',
            mode: 'immersive',
            tokenBasedBillingEnabled: false,
            plan,
            quotaUsage,
          })
        }}
      >
        Enable additional requests
      </Link>
    ) : null

  return (
    <div className={className}>
      <Banner
        variant="info"
        title="Limit reached soon"
        description={description}
        hideTitle
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        onDismiss={() => {
          onDismiss()
          sendEvent('dotcom_chat.activate', {
            target: 'QUOTA_METER_BANNER_DISMISS',
            mode: 'immersive',
            tokenBasedBillingEnabled: false,
            plan,
            quotaUsage,
          })
        }}
      />
    </div>
  )
}
